import { convexQuery } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { FunctionArgs } from "convex/server";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  storefrontAdminGetStorefrontSettings,
  storefrontAdminUpdateStorefrontSettings,
} from "~/api-refs";
import { useFileUrl } from "~/hooks/use-file-upload";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId, toStorageId } from "~/lib/convex-table-id";
import { ProductEvents } from "~/lib/posthog";

import { Form } from "../ui/form";
import { Separator } from "../ui/separator";
import { Skeleton } from "../ui/skeleton";
import { DeliveryZonesEditor } from "./delivery-zones-editor";
import { BrandingSection } from "./settings-form/branding-section";
import {
  DollarField,
  NumberField,
  SettingsSection,
  ToggleField,
} from "./settings-form/form-fields";
import {
  buildSettingsPayload,
  settingsToFormValues,
} from "./settings-form/helpers";
import {
  OnlineHoursSection,
  ScheduleOverridesSection,
} from "./settings-form/schedule-sections";
import {
  AutoAcceptSection,
  FutureOrdersSection,
  GoLiveSection,
  PauseSection,
  SlugSection,
  TipsSection,
} from "./settings-form/settings-sections";
import type { OnlineOrderingFormValues } from "./settings-form/types";
import { DEFAULT_VALUES } from "./settings-form/types";

export function OnlineOrderingSettingsForm({
  locationId,
}: {
  locationId: string;
}) {
  const { data: settings, isLoading } = useQuery(
    convexQuery(storefrontAdminGetStorefrontSettings, {
      locationId: toConvexTableId("locations", locationId),
    })
  );

  const updateMutation = useMutation({
    mutationFn: (
      data: FunctionArgs<typeof storefrontAdminUpdateStorefrontSettings>
    ) => convexClient.mutation(storefrontAdminUpdateStorefrontSettings, data),
    onSuccess: () => {
      toast.success("Settings saved");
      ProductEvents.settingsUpdated("online-ordering");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to save settings"
      );
    },
  });

  const form = useForm<OnlineOrderingFormValues>({
    values: settings ? settingsToFormValues(settings) : DEFAULT_VALUES,
  });

  // Resolve storage URLs for previews
  const logoStorageId = form.watch("logoStorageId");
  const logoUrl = useFileUrl(logoStorageId ? toStorageId(logoStorageId) : null);
  const bannerSid = form.watch("bannerStorageId");
  const bannerUrl = useFileUrl(bannerSid ? toStorageId(bannerSid) : null);
  const faviconSid = form.watch("faviconStorageId");
  const faviconUrl = useFileUrl(faviconSid ? toStorageId(faviconSid) : null);
  const ogImageSid = form.watch("ogImageStorageId");
  const ogImageUrl = useFileUrl(ogImageSid ? toStorageId(ogImageSid) : null);

  const update = () => {
    updateMutation.mutate(buildSettingsPayload(form.getValues(), locationId));
  };

  if (isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  return (
    <Form {...form}>
      <form className="space-y-6">
        {/* 1. Storefront URL */}
        <SlugSection form={form} onUpdate={update} />

        <Separator />

        {/* 2. Master Toggle */}
        <GoLiveSection form={form} onUpdate={update} />

        <Separator />

        {/* 3. Order Types */}
        <SettingsSection
          description="Choose which fulfillment methods to offer"
          title="Order Types"
        >
          <div className="space-y-3">
            <ToggleField
              description="Customers pick up at the restaurant"
              form={form}
              label="Pickup"
              name="pickupEnabled"
              onUpdate={update}
            />
            <ToggleField
              description="Orders delivered to the customer"
              form={form}
              label="Delivery"
              name="deliveryEnabled"
              onUpdate={update}
            />
            <ToggleField
              description="Customers stay in their car"
              form={form}
              label="Curbside"
              name="curbsideEnabled"
              onUpdate={update}
            />
          </div>
        </SettingsSection>

        <Separator />

        {/* 4. Timing */}
        <SettingsSection
          description="Estimated prep and delivery times"
          title="Timing"
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <NumberField
              form={form}
              label="Prep Time (min)"
              name="defaultPrepTimeMinutes"
              onUpdate={update}
            />
            <NumberField
              form={form}
              label="Delivery Time (min)"
              name="deliveryTimeMinutes"
              onUpdate={update}
            />
            <NumberField
              form={form}
              label="Slot Interval (min)"
              name="scheduleSlotIntervalMinutes"
              onUpdate={update}
            />
          </div>
        </SettingsSection>

        <Separator />

        {/* 4.5. Online Ordering Hours */}
        <OnlineHoursSection form={form} onUpdate={update} />

        <Separator />

        {/* 4.6. Schedule Overrides */}
        <ScheduleOverridesSection form={form} onUpdate={update} />

        <Separator />

        {/* 5. Future Orders */}
        <FutureOrdersSection form={form} onUpdate={update} />

        <Separator />

        {/* 5.5. Order Capacity */}
        <SettingsSection
          description="Limit orders per time slot to prevent being overwhelmed (0 = unlimited)"
          title="Order Capacity"
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <NumberField
              form={form}
              label="Max Pickup / Slot"
              name="maxPickupOrdersPerSlot"
              onUpdate={update}
            />
            <NumberField
              form={form}
              label="Max Delivery / Slot"
              name="maxDeliveryOrdersPerSlot"
              onUpdate={update}
            />
            <NumberField
              form={form}
              label="Max Curbside / Slot"
              name="maxCurbsideOrdersPerSlot"
              onUpdate={update}
            />
          </div>
        </SettingsSection>

        <Separator />

        {/* 6. Auto-Accept */}
        <AutoAcceptSection form={form} onUpdate={update} />

        <Separator />

        {/* 7. Tips */}
        <TipsSection form={form} onUpdate={update} />

        <Separator />

        {/* 8. Order Minimums */}
        <SettingsSection
          description="Set minimum order amounts and delivery fee (in dollars)"
          title="Order Minimums & Fees"
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <DollarField
              form={form}
              label="Order Minimum"
              name="minimumOrderAmount"
              onUpdate={update}
            />
            <DollarField
              form={form}
              label="Delivery Minimum"
              name="deliveryMinimumAmount"
              onUpdate={update}
            />
            <DollarField
              form={form}
              label="Delivery Fee"
              name="deliveryFee"
              onUpdate={update}
            />
          </div>
        </SettingsSection>

        {/* 8.5. Delivery Zones (only when delivery is enabled) */}
        {form.watch("deliveryEnabled") && (
          <>
            <Separator />
            <DeliveryZonesEditor locationId={locationId} />
          </>
        )}

        <Separator />

        {/* 9. Branding */}
        <BrandingSection
          bannerUrl={bannerUrl}
          faviconUrl={faviconUrl}
          form={form}
          logoUrl={logoUrl}
          ogImageUrl={ogImageUrl}
          onUpdate={update}
        />

        <Separator />

        {/* 10. Pause */}
        <PauseSection form={form} onUpdate={update} />
      </form>
    </Form>
  );
}
